import type {
  CostLimitSetting,
  CostLimitsListResponse,
  GetAdminAccountUsageResponse,
  GetAdminPresenceResponse,
  GetAdminSandboxLimitsResponse,
  GetAdminTaskDetailResponse,
  GetAdminUsageResponse,
  GetAdminUserUsageResponse,
  ListAdminTasksResponse,
  ListAdminTaskVersionsResponse,
  ListAdminUsersResponse,
  MfaResetResponse,
  UpdateAdminSandboxLimitsRequest,
  UserCostLimitDetailResponse,
} from '@kyumo/common';
import {
  CostLimitSettingSchema,
  CostLimitsListResponseSchema,
  GetAdminAccountUsageResponseSchema,
  GetAdminPresenceResponseSchema,
  GetAdminSandboxLimitsResponseSchema,
  GetAdminTaskDetailResponseSchema,
  GetAdminUsageResponseSchema,
  GetAdminUserUsageResponseSchema,
  ListAdminTasksResponseSchema,
  ListAdminTaskVersionsResponseSchema,
  ListAdminUsersResponseSchema,
  MfaResetResponseSchema,
  UserCostLimitDetailResponseSchema,
} from '@kyumo/common';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * 認証トークンを取得
 */
async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('認証トークンが取得できません');
  }
  return idToken;
}

/**
 * API リクエストを実行
 */
async function apiRequest<T>(
  path: string,
  params?: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const token = await getAuthToken();

  // API_URLの末尾スラッシュとpathの先頭スラッシュの重複を防ぐ
  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const url = new URL(`${baseUrl}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    }
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal: signal ?? null,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `API error: ${response.status}`);
  }

  return response.json();
}

/**
 * 書き込み系 API リクエストを実行（POST / PUT / DELETE）
 */
async function apiMutationRequest<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const token = await getAuthToken();

  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const url = `${baseUrl}${path}`;

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `API error: ${response.status}`);
  }

  // 204 No Content or empty body
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

// ==================== API 関数 ====================

/**
 * 使用量データを取得
 */
export async function fetchUsageData(
  month?: string,
  model?: string,
): Promise<GetAdminUsageResponse> {
  const params: Record<string, string> = {};
  if (month) params.month = month;
  if (model) params.model = model;

  const data = await apiRequest('/admin/usage', params);
  return GetAdminUsageResponseSchema.parse(data) as GetAdminUsageResponse;
}

/**
 * ユーザー別使用量データを取得
 */
export async function fetchUserUsageData(
  email: string,
  month?: string,
  model?: string,
): Promise<GetAdminUserUsageResponse> {
  const params: Record<string, string> = {};
  if (month) params.month = month;
  if (model) params.model = model;

  const data = await apiRequest(`/admin/usage/user/${encodeURIComponent(email)}`, params);
  return GetAdminUserUsageResponseSchema.parse(data) as GetAdminUserUsageResponse;
}

/**
 * アカウント別の月間利用額を取得（CSV エクスポート用・全件）
 */
export async function fetchAccountUsageData(month: string): Promise<GetAdminAccountUsageResponse> {
  const data = await apiRequest('/admin/usage/users', { month });
  return GetAdminAccountUsageResponseSchema.parse(data) as GetAdminAccountUsageResponse;
}

/**
 * ユーザー一覧を取得（全件）
 */
export async function fetchUsers(): Promise<ListAdminUsersResponse> {
  const data = await apiRequest('/admin/users');
  return ListAdminUsersResponseSchema.parse(data) as ListAdminUsersResponse;
}

/**
 * 到達性の取得の打ち切り時間。
 *
 * **再取得の応答待ちのあいだ、画面は判定を緩めている**（`use-presence.ts` を参照）。
 * 応答が返らないまま掴まれると、その間は古い「オンライン」が残り続ける。
 * 打ち切って失敗に倒し、「不明」の表示へ移らせる。
 */
const PRESENCE_REQUEST_TIMEOUT_MS = 15_000;

/**
 * 到達性（オンライン／オフライン）の一覧を取得。
 *
 * **応答には到達性の記録がある利用者しか含まれない。** 含まれない利用者は
 * オフラインと解釈してよい（`GetAdminPresenceResponseSchema` のコメントを参照）。
 *
 * 失敗はそのまま送出する。ここで空の応答へ倒すと、取得失敗が「全員オフライン」に
 * 化けて呼び出し側から区別できなくなる。
 */
export async function fetchPresence(): Promise<GetAdminPresenceResponse> {
  const data = await apiRequest(
    '/admin/presence',
    undefined,
    AbortSignal.timeout(PRESENCE_REQUEST_TIMEOUT_MS),
  );
  return GetAdminPresenceResponseSchema.parse(data) as GetAdminPresenceResponse;
}

/**
 * タスク一覧を取得（全件）
 */
export async function fetchTasks(email?: string): Promise<ListAdminTasksResponse> {
  const params: Record<string, string> = {};
  if (email) params.email = email;

  const data = await apiRequest('/admin/tasks', params);
  return ListAdminTasksResponseSchema.parse(data) as ListAdminTasksResponse;
}

/**
 * タスク詳細を取得
 */
export async function fetchTaskDetail(
  email: string,
  taskId: string,
): Promise<GetAdminTaskDetailResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}`,
  );
  return GetAdminTaskDetailResponseSchema.parse(data) as GetAdminTaskDetailResponse;
}

/**
 * タスクバージョン一覧を取得
 */
export async function fetchTaskVersions(
  email: string,
  taskId: string,
): Promise<ListAdminTaskVersionsResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}/versions`,
  );
  return ListAdminTaskVersionsResponseSchema.parse(data) as ListAdminTaskVersionsResponse;
}

/**
 * 特定バージョンのタスク詳細を取得
 */
export async function fetchTaskVersion(
  email: string,
  taskId: string,
  version: number,
): Promise<GetAdminTaskDetailResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}/versions/${version}`,
  );
  return GetAdminTaskDetailResponseSchema.parse(data) as GetAdminTaskDetailResponse;
}

/**
 * ファイルダウンロード用の署名付きURLを取得
 */
export async function fetchDownloadUrl(s3Key: string): Promise<{ downloadUrl: string }> {
  return apiRequest<{ downloadUrl: string }>('/files/download-url', { s3Key });
}

// ==================== コスト上限 API ====================

/**
 * コスト上限一覧を取得（デフォルト上限 + 全ユーザー個別上限）
 */
export async function fetchCostLimits(): Promise<CostLimitsListResponse> {
  const data = await apiRequest('/admin/cost-limits');
  return CostLimitsListResponseSchema.parse(data) as CostLimitsListResponse;
}

/**
 * サンドボックス上限（稼働時間・転送量）を取得
 */
export async function fetchSandboxLimits(): Promise<GetAdminSandboxLimitsResponse> {
  const data = await apiRequest('/admin/sandbox-limits');
  return GetAdminSandboxLimitsResponseSchema.parse(data);
}

/**
 * サンドボックス上限を設定
 */
export async function setSandboxLimits(
  limits: UpdateAdminSandboxLimitsRequest,
): Promise<GetAdminSandboxLimitsResponse> {
  const data = await apiMutationRequest('/admin/sandbox-limits', 'PUT', limits);
  return GetAdminSandboxLimitsResponseSchema.parse(data);
}

/**
 * サンドボックス上限を既定値へ戻す
 */
export async function deleteSandboxLimits(): Promise<void> {
  await apiMutationRequest('/admin/sandbox-limits', 'DELETE');
}

/**
 * デフォルトコスト上限を取得
 */
export async function fetchDefaultCostLimit(): Promise<CostLimitSetting | null> {
  const data = await apiRequest('/admin/cost-limits/default');
  return CostLimitSettingSchema.nullable().parse(data);
}

/**
 * デフォルトコスト上限を設定
 */
export async function setDefaultCostLimit(limitUSD: number): Promise<CostLimitSetting> {
  const data = await apiMutationRequest('/admin/cost-limits/default', 'PUT', { limitUSD });
  return CostLimitSettingSchema.parse(data);
}

/**
 * デフォルトコスト上限を削除
 */
export async function deleteDefaultCostLimit(): Promise<void> {
  await apiMutationRequest('/admin/cost-limits/default', 'DELETE');
}

/**
 * システム全体（全ユーザー合計）コスト上限を設定
 *
 * テナント全体に一斉に効く設定であり、0 を指定すると全ユーザーの利用が即時停止する。
 */
export async function setSystemCostLimit(limitUSD: number): Promise<CostLimitSetting> {
  const data = await apiMutationRequest('/admin/cost-limits/system', 'PUT', { limitUSD });
  return CostLimitSettingSchema.parse(data);
}

/**
 * システム全体コスト上限を削除
 */
export async function deleteSystemCostLimit(): Promise<void> {
  await apiMutationRequest('/admin/cost-limits/system', 'DELETE');
}

/**
 * ユーザー個別コスト上限詳細を取得
 */
export async function fetchUserCostLimitDetail(
  email: string,
): Promise<UserCostLimitDetailResponse> {
  const data = await apiRequest(`/admin/cost-limits/users/${encodeURIComponent(email)}`);
  return UserCostLimitDetailResponseSchema.parse(data) as UserCostLimitDetailResponse;
}

/**
 * ユーザー個別コスト上限を設定
 */
export async function setUserCostLimit(email: string, limitUSD: number): Promise<CostLimitSetting> {
  const data = await apiMutationRequest(
    `/admin/cost-limits/users/${encodeURIComponent(email)}`,
    'PUT',
    { limitUSD },
  );
  return CostLimitSettingSchema.parse(data);
}

/**
 * ユーザー個別コスト上限を削除
 */
export async function deleteUserCostLimit(email: string): Promise<void> {
  await apiMutationRequest(`/admin/cost-limits/users/${encodeURIComponent(email)}`, 'DELETE');
}

/**
 * 利用者の MFA（認証アプリの登録）をリセットし、全端末のサインインを失効させる
 */
export async function resetUserMfa(email: string): Promise<MfaResetResponse> {
  const data = await apiMutationRequest(
    `/admin/users/${encodeURIComponent(email)}/mfa-reset`,
    'POST',
  );
  return MfaResetResponseSchema.parse(data);
}
